import React from 'react';
import type { CalibrationPoint } from '../utils/programmeModel';
import ProvenanceBadge from './ProvenanceBadge';
import '../styles/CalibrationView.css';

// CalibrationView — the predicted-vs-realised calibration view of the value-proven
// board (029-value-proven-dashboard, Increment E; User Story 4, FR-011/FR-015/
// FR-016, SC-001/SC-008). A PURE view over the model's `calibration` array: it
// renders EXACTLY the CalibrationPoint objects `deriveValueProven(...).calibration`
// hands it, ONE labelled row per point, in order. It does NOT fetch, derive, or
// read the snapshot — it binds what it is GIVEN.
//
// Honesty contract:
//   • The WHOLE view is ILLUSTRATIVE (no real calibration record exists pre-E6),
//     so it renders the shared ProvenanceBadge EXACTLY ONCE (one view-level badge,
//     not one per point) — "Illustrative" + accessible name "Illustrative sample
//     data" (FR-011/SC-001).
//   • The series is VISIBLY present: one labelled row per point, with each point's
//     period label (`at`) reachable as TEXT (non-brittle — not coupled to SVG
//     geometry).
//   • Predicted vs realised distinguished by TEXT/label, never colour alone
//     (FR-015): both series are named as text (header + per-row labels).
//   • Empty state: zero points render an honest message naming the absence, NOT a
//     fabricated point and no faked "0".
//   • Reserved-colour (SC-008): NEUTRAL chrome only — the view spends NO reserved
//     coral (--ap-accent / data-reserved-colour); coral is for the urgent
//     attention item only.
//   • a11y (FR-015/FR-016): the view carries an accessible name (aria-label); the
//     series table is captioned.

const CalibrationView: React.FC<{ points: CalibrationPoint[] }> = ({ points }) => {
  if (points.length === 0) {
    return (
      <p className="ap-calibration-empty">
        No calibration record yet — the delivery loop has not run.
      </p>
    );
  }

  return (
    <section
      className="ap-calibration"
      aria-label="Illustrative predicted versus realised calibration"
    >
      <header className="ap-calibration-head">
        <span className="ap-calibration-title">Predicted vs realised</span>
        <ProvenanceBadge source="illustrative" />
      </header>

      <table className="ap-calibration-table">
        <caption className="ap-calibration-caption">
          Illustrative predicted-versus-realised calibration series
        </caption>
        <thead>
          <tr>
            <th scope="col">Period</th>
            <th scope="col">Predicted</th>
            <th scope="col">Realised</th>
          </tr>
        </thead>
        <tbody>
          {points.map((point, i) => (
            <tr key={`${point.at}-${i}`} className="ap-calibration-row">
              <th scope="row" className="ap-calibration-period">
                {point.at}
              </th>
              <td className="ap-calibration-predicted">
                <span className="ap-calibration-cell-label">Predicted</span>
                <span className="ap-calibration-cell-value">{point.predicted}</span>
              </td>
              <td className="ap-calibration-realised">
                <span className="ap-calibration-cell-label">Realised</span>
                <span className="ap-calibration-cell-value">{point.realised}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
};

export default CalibrationView;
